import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, TrendingUp, DollarSign, Link2, Users, ArrowLeft, Search, Download, BarChart3, Wallet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { BrandLogo } from "../components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mcs_afiliado_token";

function getToken() { return localStorage.getItem(TOKEN_KEY); }
function setToken(t) { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); }
function authHeaders() { const t = getToken(); return t ? { Authorization: `Bearer ${t}` } : {}; }

const LEAD_STATUSES = {
  nuevo:          { label: "Nuevo",          color: "#b45309", bg: "rgba(245,158,11,0.15)" },
  en_cotizacion:  { label: "En cotización",  color: "#1e40af", bg: "rgba(59,130,246,0.15)" },
  en_negociacion: { label: "En negociación", color: "#7c2d12", bg: "rgba(234,88,12,0.15)" },
  convertido:     { label: "Convertido",     color: "#15803d", bg: "rgba(34,197,94,0.15)" },
  perdido:        { label: "Perdido",        color: "#991b1b", bg: "rgba(239,68,68,0.15)" },
};

const clp = (n) => `$${Number(n || 0).toLocaleString("es-CL")}`;

export default function AfiliadoPortal() {
  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creds, setCreds] = useState({ usuario: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("leads");
  const nav = useNavigate();

  const load = async () => {
    if (!getToken()) { setLoading(false); return; }
    try {
      const [{ data: m }, { data: ls }] = await Promise.all([
        axios.get(`${API}/afiliados/me`, { headers: authHeaders() }),
        axios.get(`${API}/afiliados/me/leads`, { headers: authHeaders() }),
      ]);
      setMe(m); setLeads(ls || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setToken(null);
        setMe(null);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/afiliados/login`, creds);
      setToken(data.token);
      await load();
      toast.success(`Hola ${data.afiliado.nombre}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenciales inválidas");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    setToken(null);
    setMe(null);
    nav("/afiliado");
    toast.success("Sesión cerrada");
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[13px]" style={{ color: "var(--text-muted)" }}>Cargando…</div>;
  }

  // ---- Login screen ----
  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg-soft)" }}>
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <BrandLogo size={44} />
          </div>
          <form onSubmit={login} className="bg-white rounded-2xl p-7 shadow-md" style={{ border: "2px solid var(--brand-navy)" }}>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: "var(--brand-accent)" }}>Portal afiliado</div>
            <h1 className="text-2xl font-extrabold mb-5" style={{ fontFamily: "Manrope" }}>Ingresa a tu panel</h1>

            <label className="block text-[12px] font-semibold mb-1" style={{ color: "var(--text-secondary)" }}>Usuario</label>
            <input value={creds.usuario} onChange={(e) => setCreds({ ...creds, usuario: e.target.value })} className="field-input field-input--compact" placeholder="tu.usuario" required data-testid="afi-login-user"/>

            <label className="block text-[12px] font-semibold mb-1 mt-3" style={{ color: "var(--text-secondary)" }}>Contraseña</label>
            <input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className="field-input field-input--compact" required data-testid="afi-login-pass"/>

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center mt-5" data-testid="afi-login-submit">{busy ? "Ingresando…" : "Ingresar"}</button>

            <p className="text-[11px] mt-4 text-center" style={{ color: "var(--text-muted)" }}>
              ¿No tienes cuenta? Pide a MiComunidadSegura que te cree una en la Central de Afiliados.
            </p>
          </form>
        </div>
      </div>
    );
  }

  // ---- Dashboard ----
  const link = `${window.location.origin}/${me.codigo}`;
  const nuevos = leads.filter((l) => (l.lead_status || "nuevo") === "nuevo").length;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-soft)" }} data-testid="afi-portal">
      <header className="bg-white border-b" style={{ borderColor: "var(--border-medium)" }}>
        <div className="container-x py-4 flex items-center justify-between">
          <BrandLogo size={34} subline={`Portal · ${me.nombre}`} />
          <button onClick={logout} className="text-[12px] font-semibold flex items-center gap-1 hover:opacity-70" style={{ color: "var(--text-muted)" }} data-testid="afi-logout">
            <LogOut size={13}/> Salir
          </button>
        </div>
      </header>

      <main className="container-x py-8">
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "var(--brand-accent)" }}>Tu código: {me.codigo}</div>
          <h1 className="text-3xl font-extrabold mt-1" style={{ fontFamily: "Manrope" }}>Hola, {me.nombre}</h1>
          <p className="text-[13px] mt-2" style={{ color: "var(--text-muted)" }}>
            Comparte tu link: <code className="px-2 py-0.5 rounded ml-1" style={{ background: "white", border: "1px solid var(--border-medium)" }}>{link}</code>
            <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copiado"); }} className="ml-2 text-[12px] font-semibold hover:underline" style={{ color: "var(--brand-navy)" }}>Copiar</button>
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Leads totales" value={me.leads_count} icon={Link2} color="var(--brand-navy)" />
          <StatCard label="Nuevos sin atender" value={nuevos} icon={Users} color="var(--brand-accent)" />
          <StatCard label="Convertidos" value={me.convertidos} icon={TrendingUp} color="#15803d" />
          <StatCard label="Comisión pendiente" value={clp(me.comision_pendiente)} icon={DollarSign} color="#15803d" highlight />
        </div>

        <LeadsChart leads={leads} />

        <div className="bg-white rounded-xl overflow-hidden" style={{ border: "1px solid var(--border-medium)" }}>
          <div className="px-5 pt-3 border-b flex items-center gap-1" style={{ borderColor: "var(--border-medium)" }}>
            <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} icon={Users} testId="afi-tab-leads">Tus leads</TabBtn>
            <TabBtn active={tab === "comisiones"} onClick={() => setTab("comisiones")} icon={Wallet} testId="afi-tab-comisiones">Comisiones</TabBtn>
          </div>
          {tab === "leads" ? <LeadsTable leads={leads} codigo={me.codigo} /> : <ComisionesTab leads={leads} />}
        </div>

        <div className="mt-6 text-center">
          <a href="/" className="text-[12px] hover:underline inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
            <ArrowLeft size={11}/> Volver al sitio
          </a>
        </div>
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="text-[13px] font-bold px-4 py-2.5 -mb-px flex items-center gap-1.5 transition-colors"
      style={{
        color: active ? "var(--brand-navy)" : "var(--text-muted)",
        borderBottom: active ? "2px solid var(--brand-navy)" : "2px solid transparent",
        fontFamily: "Manrope",
      }}>
      <Icon size={14}/> {children}
    </button>
  );
}

// ---- Gráfico: leads por mes (últimos 6 meses) ----
function LeadsChart({ leads }) {
  const data = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("es-CL", { month: "short" }).replace(".", "");
      months.push({ key, label: label.charAt(0).toUpperCase() + label.slice(1), leads: 0, convertidos: 0 });
    }
    const idx = Object.fromEntries(months.map((m, i) => [m.key, i]));
    leads.forEach((l) => {
      const k = (l.fecha || "").slice(0, 7);
      if (k in idx) {
        months[idx[k]].leads += 1;
        if (l.convertido) months[idx[k]].convertidos += 1;
      }
    });
    return months;
  }, [leads]);

  const total6m = data.reduce((s, m) => s + m.leads, 0);

  return (
    <div className="bg-white rounded-xl p-5 mb-6" style={{ border: "1px solid var(--border-medium)" }} data-testid="afi-chart">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[15px] font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
          <BarChart3 size={15} style={{ color: "var(--brand-navy)" }}/> Rendimiento — últimos 6 meses
        </h2>
        <span className="text-[11px] font-semibold" style={{ color: "var(--text-muted)" }}>{total6m} leads en el período</span>
      </div>
      <div style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 5, left: -25, bottom: 0 }} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "rgba(30,63,174,0.05)" }} contentStyle={{ fontSize: 12, borderRadius: 8 }}
              formatter={(v, name) => [v, name === "leads" ? "Leads" : "Convertidos"]} labelStyle={{ fontWeight: 700 }} />
            <Bar dataKey="leads" name="leads" fill="var(--brand-navy, #1E3FAE)" radius={[4, 4, 0, 0]} maxBarSize={38} />
            <Bar dataKey="convertidos" name="convertidos" fill="#22c55e" radius={[4, 4, 0, 0]} maxBarSize={38} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-4 mt-2 justify-center">
        <LegendDot color="#1E3FAE" label="Leads recibidos" />
        <LegendDot color="#22c55e" label="Convertidos" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="text-[11px] flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>
      <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: color }}/>{label}
    </span>
  );
}

// ---- Tabla de leads con búsqueda, filtro por estado y export CSV ----
function LeadsTable({ leads, codigo }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== "todos" && (l.lead_status || "nuevo") !== statusFilter) return false;
      if (!term) return true;
      return [l.detalle, l.nombre, l.email, l.telefono].some((v) => (v || "").toLowerCase().includes(term));
    });
  }, [leads, q, statusFilter]);

  const exportCSV = () => {
    const header = ["Fecha", "Comunidad", "Contacto", "Email", "Teléfono", "Estado", "Comisión", "Estado comisión"];
    const rows = filtered.map((l) => [
      (l.fecha || "").slice(0, 10),
      l.detalle || "",
      l.nombre || "",
      l.email || "",
      l.telefono || "",
      (LEAD_STATUSES[l.lead_status] || LEAD_STATUSES.nuevo).label,
      l.comision_monto || "",
      l.comision_estado === "pendiente" ? "Por pagar" : l.comision_estado === "cobrada" ? "Pagado" : "",
    ]);
    const esc = (v) => `"${String(v).replaceAll('"', '""')}"`;
    const csv = "\uFEFF" + [header, ...rows].map((r) => r.map(esc).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `leads-${codigo.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`${filtered.length} leads exportados`);
  };

  return (
    <div>
      <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-b" style={{ borderColor: "var(--border-medium)" }}>
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }}/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar comunidad, contacto o email…"
            className="w-full text-[12px] rounded-lg pl-8 pr-3 py-1.5 outline-none"
            style={{ border: "1px solid var(--border-medium)" }} data-testid="afi-search-input"/>
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="text-[12px] rounded-lg px-2.5 py-1.5 bg-white outline-none"
          style={{ border: "1px solid var(--border-medium)" }} data-testid="afi-status-filter">
          <option value="todos">Todos los estados</option>
          {Object.entries(LEAD_STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={exportCSV} disabled={filtered.length === 0}
          className="text-[12px] font-semibold flex items-center gap-1.5 rounded-lg px-3 py-1.5 ml-auto disabled:opacity-40 hover:opacity-80 transition-opacity"
          style={{ border: "1px solid var(--brand-navy)", color: "var(--brand-navy)" }} data-testid="afi-export-csv">
          <Download size={12}/> Exportar CSV
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="py-12 text-center text-[13px]" style={{ color: "var(--text-muted)" }} data-testid="afi-leads-empty">
          {leads.length === 0 ? "Aún no tienes leads referidos. Comparte tu link para empezar." : "Sin resultados para tu búsqueda."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr><th>Fecha</th><th>Comunidad</th><th>Mail</th><th>Estado</th><th>Comisión</th><th>Estado comisión</th></tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const status = LEAD_STATUSES[l.lead_status] || LEAD_STATUSES.nuevo;
                return (
                  <tr key={`${l.tipo}-${l.id}`}>
                    <td className="text-[12px]" style={{ color: "var(--text-muted)" }}>{(l.fecha || "").slice(0, 10)}</td>
                    <td>
                      <div className="font-semibold text-[13px]">{l.detalle || l.nombre}</div>
                      {l.detalle && <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Contacto: {l.nombre}</div>}
                    </td>
                    <td className="text-[12px]">{l.email}<br/><span style={{ color: "var(--text-muted)" }}>{l.telefono}</span></td>
                    <td>
                      <span className="text-[10.5px] font-bold uppercase px-2 py-0.5 rounded" style={{ background: status.bg, color: status.color }}>
                        {status.label}
                      </span>
                    </td>
                    <td>
                      {l.comision_monto ? (
                        <span className="font-semibold" style={{ color: "#15803d" }}>{clp(l.comision_monto)}</span>
                      ) : (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                    <td>
                      {l.comision_estado === "pendiente" && (
                        <span className="text-[10.5px] font-bold px-2 py-0.5 rounded" style={{ background: "rgba(245,158,11,0.15)", color: "#b45309" }}>POR PAGAR</span>
                      )}
                      {l.comision_estado === "cobrada" && (
                        <span className="text-[10.5px] font-bold px-2 py-0.5 rounded" style={{ background: "rgba(34,197,94,0.15)", color: "#15803d" }}>PAGADO</span>
                      )}
                      {(!l.comision_estado || l.comision_estado === "no_aplica") && (
                        <span className="text-[10.5px]" style={{ color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Historial de comisiones ----
function ComisionesTab({ leads }) {
  const convertidos = useMemo(
    () => leads.filter((l) => l.convertido || l.comision_monto),
    [leads]
  );
  const pagado = convertidos.filter((l) => l.comision_estado === "cobrada").reduce((s, l) => s + Number(l.comision_monto || 0), 0);
  const pendiente = convertidos.filter((l) => l.comision_estado === "pendiente").reduce((s, l) => s + Number(l.comision_monto || 0), 0);

  return (
    <div data-testid="afi-comisiones">
      <div className="grid grid-cols-3 gap-3 p-5 border-b" style={{ borderColor: "var(--border-medium)" }}>
        <MiniStat label="Total histórico" value={clp(pagado + pendiente)} color="var(--brand-navy)" testId="afi-com-total"/>
        <MiniStat label="Pagado" value={clp(pagado)} color="#15803d" testId="afi-com-pagado"/>
        <MiniStat label="Por pagar" value={clp(pendiente)} color="#b45309" testId="afi-com-pendiente"/>
      </div>

      {convertidos.length === 0 ? (
        <div className="py-12 text-center text-[13px]" style={{ color: "var(--text-muted)" }} data-testid="afi-com-empty">
          Aún no tienes leads convertidos. Cuando una comunidad referida contrate, tu comisión aparecerá aquí.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr><th>Fecha lead</th><th>Comunidad</th><th>Comisión</th><th>Estado pago</th></tr>
            </thead>
            <tbody>
              {convertidos.map((l) => (
                <tr key={`c-${l.tipo}-${l.id}`}>
                  <td className="text-[12px]" style={{ color: "var(--text-muted)" }}>{(l.fecha || "").slice(0, 10)}</td>
                  <td>
                    <div className="font-semibold text-[13px]">{l.detalle || l.nombre}</div>
                    {l.detalle && <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Contacto: {l.nombre}</div>}
                  </td>
                  <td>
                    {l.comision_monto ? (
                      <span className="font-bold" style={{ color: "#15803d" }}>{clp(l.comision_monto)}</span>
                    ) : (
                      <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>Por definir</span>
                    )}
                  </td>
                  <td>
                    {l.comision_estado === "cobrada" ? (
                      <span className="text-[10.5px] font-bold px-2 py-0.5 rounded" style={{ background: "rgba(34,197,94,0.15)", color: "#15803d" }}>PAGADO</span>
                    ) : (
                      <span className="text-[10.5px] font-bold px-2 py-0.5 rounded" style={{ background: "rgba(245,158,11,0.15)", color: "#b45309" }}>POR PAGAR</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, color, testId }) {
  return (
    <div className="rounded-lg p-3" style={{ background: "var(--bg-soft)" }} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="text-[18px] font-extrabold" style={{ color, fontFamily: "Manrope" }}>{value}</div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, highlight }) {
  return (
    <div className="bg-white rounded-xl p-4" style={{ border: `1px solid ${highlight ? "rgba(34,197,94,0.3)" : "var(--border-medium)"}`, background: highlight ? "rgba(34,197,94,0.03)" : undefined }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color }}/>
        <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>{label}</span>
      </div>
      <div className="text-[22px] font-extrabold leading-tight" style={{ color, fontFamily: "Manrope" }}>{value}</div>
    </div>
  );
}
